import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getApplicationsBySearch,
  getPaginationApplicationCount,
} from "../../../actions/application.action";
import Badge from "../../../components/badge";
import { SearchBox } from "../../../components/input-functions";
import Pagination from "../../../components/pagination";
import Table, { TableColumns } from "../../../components/table";
import useQueryParams from "../../../hooks/useQueryParams";
import useScreenName from "../../../hooks/useScreenName";
import dispatch from "../../../middleware";
import { RoutePaths } from "../../../models";
import { ReduxState } from "../../../reducers";
import { TermLoanApplicationDetails } from "../../../dto";
import {
  clearFormData,
  dateToDDMMYYYY,
  getPaginationCount,
  isLoadingActive,
  splitKeyWithSpace,
} from "../../../utils";

const columns: TableColumns[] = [
  { field: "_id", label: "Application ID" },
  { field: "userName", label: "User Name" },
  { field: "createdAt", label: "Date" },
  { field: "approvedAmount", label: "Approved Amount" },
  { field: "status", label: "Status" },
];

type SearchQueryType = {
  pageNo: number;
  searchText: string;
  numberOfEntitiesPerPage: number;
};

const DEFAULT_SEARCH_QUERY_STATE: SearchQueryType = {
  pageNo: 1,
  searchText: "",
  numberOfEntitiesPerPage: 10,
};

export default function ManualReviewListPage() {
  useScreenName("Manual Review");
  const storeDispatch = useDispatch();

  const applications: TermLoanApplicationDetails[] = useSelector(
    (state: ReduxState) => state.application.applications,
  );
  const paginationCount: Record<string, number | null> = useSelector(
    (state: ReduxState) => state.application.paginationCount,
  );
  const loadingQueue = useSelector(
    (state: ReduxState) => state.application.loadingQueue,
  );

  const [{ pageNo, searchText, numberOfEntitiesPerPage }, setSearchQuery] =
    useQueryParams<SearchQueryType>(DEFAULT_SEARCH_QUERY_STATE);

  useEffect(() => {
    dispatch(
      storeDispatch,
      getApplicationsBySearch(
        clearFormData({
          numberOfEntitiesPerPage,
          pageNo,
          searchText,
          applicationType: "TERM_LOAN_APP",
          status: ["PENDING_MANUAL_REVIEW"],
        }),
      ),
    );
    dispatch(
      storeDispatch,
      getPaginationApplicationCount(
        clearFormData({
          applicationType: "TERM_LOAN_APP",
          searchText,
          status: ["PENDING_MANUAL_REVIEW"],
        }),
      ),
    );
  }, [pageNo, searchText, numberOfEntitiesPerPage, storeDispatch]);

  function handleSearch(text: string) {
    setSearchQuery((prevState) => ({ ...prevState, searchText: text, pageNo: 1 }));
  }

  function getStatusBadge(status: string) {
    if (status === "APPROVED") return <Badge type="success">Approved</Badge>;
    if (status === "REJECTED") return <Badge type="reject">Rejected</Badge>;
    return (
      <Badge type="pending" capitalize>
        {splitKeyWithSpace(status).toLowerCase()}
      </Badge>
    );
  }

  function getIdLink(appId: string) {
    const detailPath = RoutePaths.CREDIT_DECISION_MANUAL_REVIEW_DETAIL.replace(
      ":appId",
      appId,
    );
    return (
      <Link to={detailPath} className="table-link">
        {appId}
      </Link>
    );
  }

  function getTableData(apps: TermLoanApplicationDetails[]) {
    return apps.map((app) => ({
      _id: getIdLink(app._id),
      userName: app.user?.employeeName ?? "-",
      createdAt: app.createdAt ? dateToDDMMYYYY(app.createdAt) : "-",
      approvedAmount: app.approvedAmount || "-",
      status: getStatusBadge(
        typeof app.status === "string" ? app.status : app.status.data,
      ),
    }));
  }

  return (
    <>
      <div className="dashboard-container">
        <div className="d-flex flex-lg-row flex-column align-items-lg-end mb-4">
          <SearchBox
            value={searchText}
            label="Search"
            placeholder="Search by Application ID, User ID, or User Name"
            onSubmit={handleSearch}
          />
        </div>
        <Table
          loading={isLoadingActive(loadingQueue)}
          columns={columns}
          data={getTableData(applications)}
        />
      </div>
      <Pagination
        currentPage={pageNo}
        totalEntries={getPaginationCount(
          paginationCount.termLoanApplicationCount,
          numberOfEntitiesPerPage,
        )}
        numberOfEntriesPerPage={numberOfEntitiesPerPage}
        onPageChange={(pageNo) =>
          setSearchQuery((prevState) => ({ ...prevState, pageNo }))
        }
        onPerPageCountChange={(countPerPage) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            numberOfEntriesPerPage: countPerPage,
            pageNo: 1,
          }))
        }
      />
    </>
  );
}
